import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useRequireAccess,
  useTranslate,
} from "ra-core";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { EXPENSE_MESSAGES } from "./expenseRules";
import { validateCategoryName } from "./expenseCategoryRules";
import type { ExpenseCategory } from "./expenseTypes";

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

/** Single name form shared by "Agregar categoría" and "Editar". */
const CategoryNameForm = ({
  existing,
  selfId,
  initialValue,
  onSave,
  onCancel,
}: {
  existing: readonly ExpenseCategory[];
  selfId: ExpenseCategory["id"] | undefined;
  initialValue: string;
  onSave: (name: string) => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const inputId = `expense-category-name-${selfId ?? "new"}`;

  const handleSave = async () => {
    const message = validateCategoryName(value, existing, selfId);
    if (message) {
      setError(message);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(value.trim());
    } catch (saveError) {
      setError(errorMessage(saveError, translate("crm.expenses.save_error")));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
      <Label htmlFor={inputId}>
        {translate("crm.expenses.categories.name_field")}
      </Label>
      <Input
        id={inputId}
        value={value}
        disabled={isSaving}
        aria-invalid={error != null}
        onChange={(event) => {
          setValue(event.target.value);
          setError(undefined);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void handleSave();
          }
        }}
      />
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11"
          disabled={isSaving}
          onClick={onCancel}
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
          onClick={() => void handleSave()}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};

/**
 * Almikia Epic EG (TASK-044): "Categorías de gastos". The four expense roles
 * read the catalogue; only direccion sees the write controls (add, rename,
 * archive / reactivate, inline). Categories are archived, never deleted, and a
 * duplicate name is rejected. Mirrors `ProfileOptionList`.
 */
export const ExpenseCategoriesPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { isPending: isCheckingAccess } = useRequireAccess({
    resource: "expense_categories",
    action: "list",
  });
  const { canAccess: canWrite } = useCanAccess({
    resource: "expense_categories",
    action: "edit",
  });
  const { data, isPending, error, refetch } = useGetList<ExpenseCategory>(
    "expense_categories",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !isCheckingAccess },
  );

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<ExpenseCategory["id"] | null>(
    null,
  );
  const [archiveTarget, setArchiveTarget] = useState<ExpenseCategory | null>(
    null,
  );
  const [isArchiving, setIsArchiving] = useState(false);

  const handleCreate = async (name: string) => {
    await dataProvider.create("expense_categories", { data: { name } });
    setIsAdding(false);
    refetch();
    notify("crm.expenses.categories.created_notice");
  };

  const handleRename = async (category: ExpenseCategory, name: string) => {
    await dataProvider.update("expense_categories", {
      id: category.id,
      data: { name },
      previousData: category,
    });
    setEditingId(null);
    refetch();
    notify("crm.expenses.categories.updated_notice");
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const wasArchived = archiveTarget.archived_at != null;
    setIsArchiving(true);
    try {
      await dataProvider.update("expense_categories", {
        id: archiveTarget.id,
        data: { archived_at: wasArchived ? null : new Date().toISOString() },
        previousData: archiveTarget,
      });
      notify(
        wasArchived
          ? "crm.expenses.categories.reactivated_notice"
          : "crm.expenses.categories.archived_notice",
      );
      refetch();
    } catch (writeError) {
      notify(errorMessage(writeError, EXPENSE_MESSAGES.forbidden), {
        type: "error",
      });
    } finally {
      setIsArchiving(false);
      setArchiveTarget(null);
    }
  };

  const title = translate("crm.expenses.categories.page_title");
  const isArchivedTarget = archiveTarget?.archived_at != null;
  const ordered = [...(data ?? [])].sort(
    (a, b) =>
      Number(a.archived_at != null) - Number(b.archived_at != null) ||
      a.name.localeCompare(b.name),
  );

  let body;
  if (error) {
    body = <ListErrorState onRetry={() => refetch()} />;
  } else if (isCheckingAccess || isPending || !data) {
    body = <ListCardsSkeleton rows={3} />;
  } else if (ordered.length === 0) {
    body = (
      <ListEmptyState title={translate("crm.expenses.categories.empty")} />
    );
  } else {
    body = (
      <div className="divide-y rounded-md border">
        {ordered.map((category) => {
          const isArchived = category.archived_at != null;
          if (editingId === category.id) {
            return (
              <div key={category.id} className="p-3">
                <CategoryNameForm
                  existing={ordered}
                  selfId={category.id}
                  initialValue={category.name}
                  onSave={(name) => handleRename(category, name)}
                  onCancel={() => setEditingId(null)}
                />
              </div>
            );
          }
          return (
            <div
              key={category.id}
              data-testid="expense-category-row"
              className="flex flex-wrap items-center gap-3 p-3"
            >
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="truncate font-medium">{category.name}</span>
                {isArchived ? (
                  <StatusPill
                    tone="not-ok"
                    text={translate("crm.expenses.categories.archived_marker")}
                  />
                ) : null}
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="min-h-11"
                    onClick={() => setEditingId(category.id)}
                  >
                    <Pencil className="mr-1 size-4" />
                    {translate("crm.expenses.categories.edit_action")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isArchived ? "default" : "destructive"}
                    className="min-h-11"
                    onClick={() => setArchiveTarget(category)}
                  >
                    {translate(
                      isArchived
                        ? "crm.expenses.categories.reactivate_action"
                        : "crm.expenses.categories.archive_action",
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-8">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <Card data-testid="expense-categories">
        <CardContent className="space-y-3 p-4">
          {canWrite ? (
            <div className="flex justify-end">
              {isAdding ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="min-h-11"
                  onClick={() => setIsAdding(true)}
                >
                  <Plus className="mr-1 size-4" />
                  {translate("crm.expenses.categories.add_action")}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("crm.expenses.categories.read_only")}
            </p>
          )}
          {isAdding ? (
            <CategoryNameForm
              existing={ordered}
              selfId={undefined}
              initialValue=""
              onSave={handleCreate}
              onCancel={() => setIsAdding(false)}
            />
          ) : null}
          {body}
        </CardContent>
        <Confirm
          isOpen={archiveTarget != null}
          loading={isArchiving}
          title={
            isArchivedTarget
              ? "crm.expenses.categories.confirm_reactivate_title"
              : "crm.expenses.categories.confirm_archive_title"
          }
          content={
            isArchivedTarget
              ? "crm.expenses.categories.confirm_reactivate_body"
              : "crm.expenses.categories.confirm_archive_body"
          }
          confirm={
            isArchivedTarget
              ? "crm.expenses.categories.reactivate_action"
              : "crm.expenses.categories.archive_action"
          }
          confirmColor={isArchivedTarget ? "primary" : "warning"}
          onConfirm={handleArchiveConfirm}
          onClose={() => setArchiveTarget(null)}
        />
      </Card>
    </div>
  );
};
